import type { ReactNode } from "react";
import { cn } from "../../lib/cn";
import { Icon, type IconName } from "./Icon";
import { TapTarget } from "./TapTarget";

export interface TabItem<T extends string> {
  value: T;
  label: ReactNode;
  icon?: IconName;
  count?: number;
}

export interface TabsProps<T extends string> {
  items: TabItem<T>[];
  value: T;
  onChange: (value: T) => void;
  className?: string;
}

export function Tabs<T extends string>({ items, value, onChange, className }: TabsProps<T>) {
  return (
    <div
      role="tablist"
      className={cn(
        // -mx-4 px-4 pa mobil lar fane-raden strekke seg ut til Container-kantene
        // slik at horisontal scroll fungerer naturlig nar fanene ikke far plass.
        "scrollbar-none -mx-4 flex items-center gap-3 overflow-x-auto border-ink-200 border-b px-4 sm:-mx-1 sm:gap-2 sm:px-1",
        className,
      )}
    >
      {items.map((item) => {
        const isActive = item.value === value;
        return (
          <TapTarget
            key={item.value}
            role="tab"
            aria-selected={isActive}
            onClick={() => onChange(item.value)}
            className={cn(
              "relative inline-flex h-11 shrink-0 items-center gap-2 whitespace-nowrap rounded-t px-4 font-medium text-sm transition-colors",
              isActive ? "text-ink-900" : "text-ink-500 hover:text-ink-800",
            )}
          >
            {item.icon && <Icon name={item.icon} size="xs" />}
            {item.label}
            {typeof item.count === "number" && (
              <span
                className={cn(
                  "ml-0.5 inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded px-1.5 font-medium text-[11px]",
                  isActive ? "bg-ink-200 text-ink-800" : "bg-ink-100 text-ink-600",
                )}
              >
                {item.count}
              </span>
            )}
            {isActive && <span className="absolute right-0 -bottom-px left-0 h-0.5 bg-ink-900" />}
          </TapTarget>
        );
      })}
    </div>
  );
}
